import {FC, useCallback, useMemo} from 'react';
import {useNavigate} from 'react-router';
import {Formik, Form} from 'formik';
import * as Yup from 'yup';
import {toastr} from '@/utils/toastHelper';
import {Col, Row} from "react-bootstrap";
import {FormPageFooter, FormPageHeader} from "@components/falcon/common/forms";
import {BaseInfoBlock, RewardBlock} from "@components/consumer/daily/forms";
import {BalanceGiftInfo, ItemGiftInfo, PutDailyGiftModel} from "@models/consumer/daily";
import {EGiftType} from "@interfaces/user/daily";
import ConsumerDailyService from "@api/consumer/consumerDailyService";

interface AddDailyGiftFormValues {
    name: string;
    days: number;
    isActive: boolean;
    repeat: boolean;
    oneTime: boolean;
    balance?: number;
    itemId?: string;
    count?: number;
    type: EGiftType;
    groupId: string | null;
}

const AddDailyGiftForm: FC = () => {

    const navigate = useNavigate();

    const validationSchema = useMemo(() => Yup.object({
        name: Yup.string()
            .min(3)
            .max(100)
            .required(),
        days: Yup.number()
            .min(1)
            .max(1000)
            .required(),
        isActive: Yup.boolean()
            .required(),
        repeat: Yup.boolean()
            .required(),
        oneTime: Yup.boolean()
            .required(),
        type: Yup.mixed<EGiftType>()
            .oneOf(Object.values(EGiftType))
            .required(),
        balance: Yup.number()
            .when('type', {
                is: EGiftType.BalanceGift,
                then: schema => schema
                    .max(10000000)
                    .required()
            }),
        itemId: Yup.string()
            .when('type', {
                is: EGiftType.ItemGift,
                then: schema => schema
                    .required()
            }),
        count: Yup.number()
            .when('type', {
                is: EGiftType.ItemGift,
                then: schema => schema
                    .min(1)
                    .max(2000000000)
                    .required()
            }),
        groupId: Yup.string()
            .nullable()
            .notRequired()
    }), []);

    const initialValues: AddDailyGiftFormValues = {
        name: '',
        days: 1,
        isActive: false,
        repeat: false,
        oneTime: true,
        type: EGiftType.BalanceGift,
        itemId: '',
        count: 1,
        balance: 0,
        groupId: null
    };

    const onSubmit = useCallback(async (values: AddDailyGiftFormValues) => {
        const info = values.type === EGiftType.BalanceGift ? {
            balance: values.balance || 0
        } as BalanceGiftInfo : {
            itemId: values.itemId || '',
            count: values.count || 0
        } as ItemGiftInfo;

        const model: PutDailyGiftModel = {
            name: values.name,
            days: values.days,
            isActive: values.isActive,
            repeat: values.repeat,
            oneTime: values.oneTime,
            groupId: values.groupId,
            info
        };

        const api = new ConsumerDailyService();
        const result = values.type === EGiftType.BalanceGift ?
            await api.putBalanceGift(model) :
            await api.putItemGift(model);

        if (result.success) {
            toastr.success("Подарки", "Подарок успешно создан");
            navigate(`/consumer/daily/${result.result!.id}/`, { replace: true });
        }
        else {
            if (result.errorCode === 'DisabledItem') {
                toastr.error("Подарки", "Данный предмет недоступен");
            }
            else if (result.errorCode === 'InvalidGiftType') {
                toastr.error("Подарки", "Неизвестный тип подарка");
            }
            else {
                toastr.error("Подарки", "Что-то пошло не так");
            }
        }
    }, [navigate]);

    return (
        <Formik
            initialValues={initialValues}
            onSubmit={onSubmit}
            validationSchema={validationSchema}
        >
            <Form>
                <Row className="g-3">
                    <Col xs={12}>
                        <FormPageHeader title="Создание подарка" />
                    </Col>
                    <Col lg={8}>
                        <BaseInfoBlock />
                    </Col>
                    <Col lg={4} className="sticky-sidebar">
                        <RewardBlock />
                    </Col>
                    <Col>
                        <FormPageFooter cancelUrl={`/consumer/daily/`} />
                    </Col>
                </Row>
            </Form>
        </Formik>
    )
}

export default AddDailyGiftForm;
